import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import {
  Box,
  debounce,
  FormControl,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  useTheme,
} from '@mui/material';
import { useTranslate } from '@si/utils';
import type { ChangeEvent, ReactNode } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';

export type TextInputProps = Pick<
  MuiTextFieldProps,
  | 'autoFocus'
  | 'disabled'
  | 'id'
  | 'multiline'
  | 'minRows'
  | 'maxRows'
  | 'onBlur'
  | 'onChange'
  | 'onKeyDown'
  | 'required'
  | 'rows'
> & {
  autoComplete?: 'email' | 'given-name' | 'family-name' | 'name' | 'url' | 'off';
  debounceMs?: number;
  endAdornment?: ReactNode;
  errorText?: string;
  helperText?: string;
  slotProps?: MuiTextFieldProps['slotProps'];
  inputWidth?: '10rem' | '15rem' | '20rem' | '25rem' | '40rem' | '100%';
  label: string;
  overrideVisited?: boolean;
  startAdornment?: ReactNode;
  type?: 'text' | 'email' | 'url';
  value: string;
  isClearable?: boolean;
};

export function TextInput({
  autoComplete = 'off',
  autoFocus = false,
  disabled,
  debounceMs = 0,
  endAdornment,
  errorText = '',
  helperText = '',
  id,
  inputWidth = '100%',
  label,
  multiline = false,
  minRows = 4,
  maxRows,
  onBlur,
  onChange,
  onKeyDown,
  overrideVisited = false,
  required = false,
  rows,
  startAdornment,
  type = 'text',
  value,
  isClearable = false,
  ...props
}: TextInputProps) {
  const { palette } = useTheme();
  const { translate, t } = useTranslate();
  const generatedId = useId();

  const [userInput, setUserInput] = useState<string>(value);
  const [visited, setVisited] = useState(false);
  const onChangeRef = useRef(onChange);
  const pendingChangeEventRef = useRef<ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null>(
    null
  );

  const textFieldId = id ?? generatedId;
  const labelId = `${textFieldId}-label`;
  const helperTextId = `${textFieldId}-helper-text`;
  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;

  const showError = !!errorText && (visited || overrideVisited);

  const onChangeWithDebounce = useMemo(() => {
    return debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      pendingChangeEventRef.current = null;
      onChangeRef.current?.(event);
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    return () => {
      onChangeWithDebounce.clear();
    };
  }, [onChangeWithDebounce]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setUserInput(value);
    pendingChangeEventRef.current = null;
    onChangeWithDebounce.clear();
  }, [onChangeWithDebounce, value]);

  const commitPendingChange = useCallback(() => {
    if (!debounceMs || !pendingChangeEventRef.current) {
      return;
    }

    const pendingChangeEvent = pendingChangeEventRef.current;
    pendingChangeEventRef.current = null;
    onChangeWithDebounce.clear();
    onChangeRef.current?.(pendingChangeEvent);
  }, [debounceMs, onChangeWithDebounce]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setUserInput(event.target.value);
    if (!debounceMs && onChange) {
      onChange(event);
    } else {
      pendingChangeEventRef.current = event;
      onChangeWithDebounce(event);
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    commitPendingChange();
    setVisited(true);
    onBlur?.(event);
  }

  const disabledTextColor = palette.text.disabled;
  const disabledFieldBackground = palette.action.disabledBackground ?? palette.surface;

  return (
    <FormControl fullWidth sx={{ maxWidth }}>
      <FormLabel
        componentId={textFieldId}
        id={labelId}
        label={label}
        required={required}
        valid={errorText.length === 0}
        disabled={!!disabled}
      />

      <MuiTextField
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        error={!!errorText && (visited || overrideVisited)}
        id={textFieldId}
        name={textFieldId}
        minRows={multiline && rows === undefined ? minRows : undefined}
        maxRows={multiline && rows === undefined ? maxRows : undefined}
        multiline={multiline}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onChange={handleChange}
        required={required}
        rows={rows}
        sx={{
          width: inputWidth,
          maxWidth: '100%',
          '& .MuiInputBase-input': {
            padding: '8px 12px',
          },
          '& .MuiInputBase-multiline': {
            padding: '0rem',
          },
          '& .MuiInputBase-root.Mui-disabled': {
            backgroundColor: disabledFieldBackground,
          },
          '& .MuiInputBase-input.Mui-disabled': {
            color: disabledTextColor,
            WebkitTextFillColor: disabledTextColor,
          },
        }}
        type={type}
        value={userInput}
        variant="outlined"
        {...props}
        slotProps={{
          ...props.slotProps,
          input: {
            endAdornment: isClearable ? (
              <Box position="relative" right="-14px">
                <IconButton
                  aria-label={translate(t.clear)}
                  onClick={() =>
                    handleChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)
                  }
                >
                  <ClearOutlinedIcon />
                </IconButton>
              </Box>
            ) : endAdornment ? (
              <InputAdornment position="end">{endAdornment}</InputAdornment>
            ) : undefined,
            ...props.slotProps?.input,
            startAdornment: startAdornment ? (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ) : undefined,
          },
          htmlInput: {
            ...props.slotProps?.htmlInput,
            'aria-describedby': helperTextId,
            'aria-labelledby': labelId,
            padding: '0.5rem',
          },
        }}
      />

      <FormHelperText
        id={helperTextId}
        showError={showError}
        text={showError ? errorText : helperText}
        disabled={!!disabled}
      />
    </FormControl>
  );
}
